import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { joinSquad, createSquad, sendWelcomeMessage, getSquadDetails } from '../services/squadApi';
import { trackEvent, trackButtonClick } from '../utils/analytics';

export function useSquadActions(eventId, squadId, options = {}) {
  const { onJoinSuccess } = options;
  const navigate = useNavigate();
  const [attendeeName, setAttendeeName] = useState('');
  const [introText, setIntroText] = useState('');
  const [firstTimeVenue, setFirstTimeVenue] = useState(null);
  const [isJoining, setIsJoining] = useState(false);
  const [isCreatingNew, setIsCreatingNew] = useState(false);

  const handleJoin = async (
    squadDetails,
    attendeeUuid,
    authToken,
    refreshSquadDetails
  ) => {
    if (!attendeeName.trim()) {
      alert('Please enter your name');
      trackEvent('Join Squad - Validation Error', {
        event_id: eventId,
        squad_id: squadId,
        error_type: 'missing_name',
      });
      return;
    }

    if (!squadDetails) return;

    setIsJoining(true);
    trackButtonClick('Join Squad', {
      event_id: eventId,
      squad_id: squadId,
      event_name: squadDetails.event_name,
      squad_number: squadDetails.squad_number,
      current_member_count: squadDetails.member_count,
    });

    try {
      const { squadId: newSquadId, attendeeUuid: returnedUuid } = await joinSquad({
        eventId: squadDetails.event_id,
        eventName: squadDetails.event_name,
        squadId,
        attendeeUuid,
        attendeeName: attendeeName.trim(),
        introText: introText.trim() || null,
        firstTimeVenue: firstTimeVenue,
        accessToken: authToken,
      });

      // Store attendee name in localStorage for future use
      if (attendeeName.trim()) {
        localStorage.setItem('attendee_name', attendeeName.trim());
      }

      // If the API returned a different UUID (e.g., from authenticated auth), store it
      if (returnedUuid && returnedUuid !== attendeeUuid) {
        localStorage.setItem('supabase_user_id', returnedUuid);
        // Trigger a storage event so useAuth picks it up
        window.dispatchEvent(new Event('storage'));
        // Also force a small delay to ensure state updates
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      // Track successful join
      trackEvent('Squad Joined', {
        event_id: eventId,
        squad_id: newSquadId,
        original_squad_id: squadId,
        event_name: squadDetails.event_name,
        was_redirected: newSquadId !== squadId,
        uuid_changed: returnedUuid && returnedUuid !== attendeeUuid,
      });

      // If redirected to a different squad, post welcome message then redirect
      if (newSquadId !== squadId) {
        trackEvent('Squad Redirected', {
          from_squad_id: squadId,
          to_squad_id: newSquadId,
          event_id: eventId,
        });
        try {
          const redirectedSquadDetails = await getSquadDetails(newSquadId);
          if (redirectedSquadDetails) {
            await sendWelcomeMessage({
              squad_id: newSquadId,
              attendee_uuid: returnedUuid || attendeeUuid,
              attendee_name: attendeeName.trim(),
              intro_text: introText.trim() || null,
              first_time_venue: firstTimeVenue,
              member_count: redirectedSquadDetails.member_count,
              event_name: redirectedSquadDetails.event_name,
            });
          }
        } catch (welcomeError) {
          console.error('Failed to send welcome message on redirect:', welcomeError);
        }
        navigate(`/e/${eventId}/s/${newSquadId}`);
      } else {
        // Wait a moment for database to update captain assignment
        await new Promise(resolve => setTimeout(resolve, 300));
        
        // Refresh squad details to show updated member list and captain status
        const updatedSquadDetails = await refreshSquadDetails();
        
        // Send welcome message after successful join (only if we have updated squad details)
        if (updatedSquadDetails) {
          try {
            await sendWelcomeMessage({
              squad_id: newSquadId,
              attendee_uuid: returnedUuid || attendeeUuid,
              attendee_name: attendeeName.trim(),
              intro_text: introText.trim() || null,
              first_time_venue: firstTimeVenue,
              member_count: updatedSquadDetails.member_count,
              event_name: squadDetails.event_name,
            });
          } catch (welcomeError) {
            // Log but don't fail the join if welcome message fails
            console.error('Failed to send welcome message:', welcomeError);
          }
        }

        // Refresh chat so the welcome message appears (realtime may not deliver it in time)
        if (typeof onJoinSuccess === 'function') {
          await new Promise((r) => setTimeout(r, 400));
          onJoinSuccess();
        }
        
        setAttendeeName(''); // Clear name input
        setIntroText(''); // Clear intro text
        setFirstTimeVenue(null); // Clear first time venue selection
        
        // If UUID changed, reload the page to ensure everything syncs
        if (returnedUuid && returnedUuid !== attendeeUuid) {
          window.location.reload();
          return;
        }
      }
    } catch (error) {
      console.error('Error joining squad:', error);
      alert('Failed to join squad. Please try again.');
      trackEvent('Join Squad - Error', {
        event_id: eventId,
        squad_id: squadId,
        error_message: error instanceof Error ? error.message : 'Unknown error',
      });
    } finally {
      setIsJoining(false);
    }
  };

  const handleCreateNewSquad = async (squadDetails) => {
    if (!squadDetails) return;

    setIsCreatingNew(true);
    trackButtonClick('Create New Squad', {
      event_id: eventId,
      original_squad_id: squadId,
      event_name: squadDetails.event_name,
      original_squad_number: squadDetails.squad_number,
    });

    try {
      const { squadId: newSquadId } = await createSquad({
        eventId: squadDetails.event_id,
        eventName: squadDetails.event_name,
        createAuthToken: true,
      });
      
      trackEvent('New Squad Created', {
        event_id: eventId,
        original_squad_id: squadId,
        new_squad_id: newSquadId,
        event_name: squadDetails.event_name,
      });
      
      // Navigate to the new squad page
      navigate(`/e/${squadDetails.event_id}/s/${newSquadId}`);
      setIsCreatingNew(false);
    } catch (error) {
      console.error('Error creating new squad:', error);
      alert('Failed to create new squad. Please try again.');
      trackEvent('Create New Squad - Error', {
        event_id: eventId,
        original_squad_id: squadId,
        error_message: error instanceof Error ? error.message : 'Unknown error',
      });
      setIsCreatingNew(false);
    }
  };

  return {
    attendeeName,
    setAttendeeName,
    introText,
    setIntroText,
    firstTimeVenue,
    setFirstTimeVenue,
    isJoining,
    isCreatingNew,
    handleJoin,
    handleCreateNewSquad,
  };
}
